import * as React from 'react';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import type { Echo } from '@/types';
import { HeaderCredits } from '../../common/HeaderCredits';
import { ClearChatModal } from './ClearChatModal';
import { ClearChatButton } from './ClearChatButton';
import burgerIcon from '@/assets/images/svg/burger_icon.svg';
import { VideoModeLayout } from './VideoModeLayout';
import { NormalModeLayout } from './NormalModeLayout';
import { featureFlags } from '@/utils/featureFlags';

export interface ChatHeaderProps {
  echo: Echo;
  formattedFans: string;
  onClear?: (() => void) | undefined;
  onClearModalOpen: () => void;
  onClearModalClose: () => void;
  isClearModalOpen: boolean;
  disabled?: boolean | undefined;
  onOpenSidebar?: (() => void) | undefined;
  isTransitioning?: boolean | undefined;
  children?: React.ReactNode | undefined;
  isAudioEnabled?: boolean | undefined;
  onAudioToggle: () => void;
  isVideoEnabled?: boolean | undefined;
  onVideoToggle: () => void;
  videoMessagesEnabled?: boolean | undefined;
  compact?: boolean | undefined;
}

export const ChatHeader: React.FC<ChatHeaderProps> = ({
  echo,
  formattedFans,
  onClear,
  onClearModalOpen,
  onClearModalClose,
  isClearModalOpen,
  disabled,
  onOpenSidebar,
  isTransitioning = false,
  children,
  isAudioEnabled = false,
  onAudioToggle,
  isVideoEnabled = false,
  onVideoToggle,
  videoMessagesEnabled = false,
  compact = false,
}) => {
  const avatarSrc = echo.imageUrl;
  const avatarModeKey = `echo-avatar-mode:${echo.id}`;
  const preferVideo = React.useMemo(() => {
    try {
      return localStorage.getItem(avatarModeKey) !== 'photo';
    } catch {
      return true;
    }
  }, [avatarModeKey]);
  const videoAvatarSrc = preferVideo
    ? (echo.idleVideoUrl ?? echo.videoAvatarUrl ?? undefined)
    : undefined;
  const isGroupChat = echo.category === 'Group Chat';
  const isVideoMode = featureFlags.videoEnabled && isVideoEnabled && videoMessagesEnabled;
  const idleVideoSrc = echo.idleVideoUrl ?? echo.videoAvatarUrl ?? null;

  const handleConfirmClear = () => {
    if (onClear) {
      onClear();
    }
    onClearModalClose();
  };

  const headerSpacing = isVideoMode
    ? 'gap-0 xl:px-[48px] xl:pt-4'
    : compact
      ? 'gap-3 xl:px-4 xl:py-3'
      : 'gap-6 xl:p-4 xl:px-[48px]';

  return (
    <>
      <header
        className={clsx(
          'relative z-20 flex w-full flex-col p-0',
          'bg-brand-840',
          'transition-all duration-300 ease-in-out',
          isVideoMode && 'overflow-hidden',
          headerSpacing
        )}
      >
        <div
          className={clsx(
            'absolute bottom-0 z-0 h-0 w-full bg-transparent shadow-[0_0_25px_20px_rgba(46,34,80,1)] transition-opacity duration-300',
            isTransitioning ? 'opacity-0' : 'opacity-100',
            'xl:hidden'
          )}
        />

        {/* ── Mobile Controls (always on top) ── */}
        <div className='absolute inset-x-0 top-0 z-30 flex items-center justify-between bg-brand-840 p-4 xl:hidden'>
          {onOpenSidebar ? (
            <button
              onClick={onOpenSidebar}
              className='flex size-10 items-center justify-center rounded-full transition-colors hover:bg-white/10'
              aria-label='Open sidebar'
            >
              <img src={burgerIcon} alt='Menu' className='size-6' />
            </button>
          ) : (
            <div />
          )}

          <div className='flex items-center gap-2'>
            <HeaderCredits />
            {onClear && <ClearChatButton onClick={onClearModalOpen} disabled={disabled} />}
          </div>
        </div>

        <AnimatePresence mode='wait'>
          {isVideoMode ? (
            <motion.div
              key='video-mode'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <VideoModeLayout
                echo={echo}
                avatarSrc={avatarSrc}
                idleVideoSrc={idleVideoSrc}
                formattedFans={formattedFans}
                isGroupChat={isGroupChat}
                isAudioEnabled={isAudioEnabled}
                onAudioToggle={onAudioToggle}
                isVideoEnabled={isVideoEnabled}
                onVideoToggle={onVideoToggle}
                onClear={onClear}
                onClearModalOpen={onClearModalOpen}
                disabled={disabled}
              />
            </motion.div>
          ) : (
            <motion.div
              key='normal-mode'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <NormalModeLayout
                echo={echo}
                avatarSrc={avatarSrc}
                videoAvatarSrc={videoAvatarSrc}
                formattedFans={formattedFans}
                isGroupChat={isGroupChat}
                isAudioEnabled={isAudioEnabled}
                onAudioToggle={onAudioToggle}
                isVideoEnabled={isVideoEnabled}
                onVideoToggle={onVideoToggle}
                videoMessagesEnabled={videoMessagesEnabled}
                onClear={onClear}
                onClearModalOpen={onClearModalOpen}
                disabled={disabled}
                compact={compact}
              />
            </motion.div>
          )}
        </AnimatePresence>
        {children && <div className='animate-slide-in relative z-10 w-full'>{children}</div>}
      </header>

      <ClearChatModal
        isOpen={isClearModalOpen}
        onClose={onClearModalClose}
        onConfirm={handleConfirmClear}
      />
    </>
  );
};

ChatHeader.displayName = 'ChatHeader';
